import { DomainCard, Eyebrow } from "@appcaire/ui";
import { Mail, MapPin } from "lucide-react";

type ContactFounderVisualProps = { isEn: boolean };

/**
 * "The person who answers" hero visual for /kontakt — founder photo plus a
 * contact card and a call-duration card, composed directly on the dark hero
 * background (no outer white/ink shell, unlike the other two hero visuals).
 */
export function ContactFounderVisual({ isEn }: ContactFounderVisualProps) {
  const t = isEn
    ? {
        alt: "Björn Evers, founder of Caire, who takes questions about home care planning",
        role: "Founder. Started Caire after firsthand experience in home care.",
        address: "Tändkulevägen 33, Nacka",
        durationLabel: "Walkthrough takes",
        durationFigure: "30 min",
        durationCaption: "We look at your schedule, not a generic demo.",
      }
    : {
        alt: "Björn Evers, grundare av Caire, som tar emot frågor om hemtjänstplanering",
        role: "Grundare. Startade Caire efter egen erfarenhet av hemtjänst.",
        address: "Tändkulevägen 33, Nacka",
        durationLabel: "Genomgången tar",
        durationFigure: "30 min",
        durationCaption:
          "Vi tittar på er besöksplanering, inte på en generisk demo.",
      };

  return (
    <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-2">
      <img
        src="/images/optimized/bjorn-evers.jpg"
        alt={t.alt}
        className="h-72 w-full rounded-2xl object-cover object-top shadow-on-ink lg:h-full"
      />

      <div className="flex flex-col gap-3">
        <DomainCard
          domain="human"
          surface="white"
          className="gap-0 rounded-xl px-5 py-4 shadow-on-ink"
        >
          <div className="text-base font-semibold text-caire-ink">
            Björn Evers
          </div>
          <p className="mt-1 text-xs leading-normal text-warm">{t.role}</p>
          <div className="my-3.5 border-t border-caire-hairline" />
          <div className="flex flex-col gap-2">
            <a
              href="mailto:info@caire.se"
              className="flex items-center gap-2.5 text-xs text-slate transition-colors hover:text-caire-action"
            >
              <Mail className="h-3.5 w-3.5 shrink-0 text-caire-action" />
              info@caire.se
            </a>
            <div className="flex items-center gap-2.5 text-xs text-slate">
              <MapPin className="h-3.5 w-3.5 shrink-0 text-caire-action" />
              {t.address}
            </div>
          </div>
        </DomainCard>

        <DomainCard
          domain="human"
          surface="white"
          className="gap-0 rounded-xl p-4 shadow-on-ink"
        >
          <Eyebrow as="div">{t.durationLabel}</Eyebrow>
          <div className="mt-1 font-mono text-2xl font-semibold tabular-nums text-caire-ink">
            {t.durationFigure}
          </div>
          <p className="mt-1 text-xs leading-normal text-slate">
            {t.durationCaption}
          </p>
        </DomainCard>
      </div>
    </div>
  );
}

export default ContactFounderVisual;
